import {getMedicationProfileFormDefaultValues} from '@/modules/drug-catalog/medication-profiles/functions'
import {MedicationProfileFormSchema} from '@/modules/drug-catalog/medication-profiles/schemas'
import {DrugItemFormSchema} from '@/modules/drug-catalog/schemas'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {CatalogType, DoseLimitsMatrix} from '@cypro/engine/database/schema'
import {DrugCatalogItem, TradeName} from '@cypro/engine/schema'
import {SelectItem} from '@cypro/ui/components/form/select'
export function mapDrugItemsToSelectItems(items: DrugCatalogItem[]): SelectItem[] {
  return items.map(s => ({value: s.id, label: s.name}))
}

export function getNewFormMedicationProfiles(
  oldValue: MedicationProfileFormSchema[],
  newItem: MedicationProfileFormSchema,
  index: number
) {
  if (index < 0) {
    return [...oldValue, newItem]
  }

  return oldValue.map((p, i) => {
    if (i === index) return newItem
    return p
  })
}

export function getNewFormStabilityRules(
  oldValue: StabilityRuleFormSchema[],
  newItem: StabilityRuleFormSchema,
  index: number
) {
  if (newItem.defaultRule) {
    oldValue = oldValue.map(rule => ({...rule, defaultRule: false}))
  }

  if (index < 0) {
    return [...oldValue, newItem]
  }

  return oldValue.map((p, i) => {
    if (i === index) return newItem
    return p
  })
}

export function getDrugItemFormDefaultValues(drugItem: DrugCatalogItem | undefined): DrugItemFormSchema {
  return {
    categoriesIds: drugItem?.categories?.map(item => item.drugCategoryId) || [],
    code: drugItem?.code || '',
    name: drugItem?.name || '',
    atcCode: drugItem?.atcCode || '',
    description: drugItem?.description || '',
    nationalId: drugItem?.nationalDrugId || '',
    prescribingName: drugItem?.prescribingName || '',
    medicationProfiles: drugItem?.medicationProfiles?.map(getMedicationProfileFormDefaultValues) || [],
  }
}

function checkCatalogType<T extends CatalogType>(input: unknown, desired: T): input is T {
  if (!input) return false
  if (typeof input === 'string') return input === desired
  if (typeof input === 'object' && 'type' in input) return input.type === desired
  return false
}

export const isTypeActiveSubstance = (input: unknown) => checkCatalogType(input, CatalogType.enum.ACTIVE_SUBSTANCE)
export const isTypeSupportiveMedication = (input: unknown) =>
  checkCatalogType(input, CatalogType.enum.SUPPORTIVE_MEDICATION)

const DOSE_LIMIT_ROWS = ['minSingleDose', 'maxSingleDose', 'maxPerDay', 'maxPerCycle', 'maxLifetime'] as const
const DOSE_LIMIT_COLS = ['absolute', 'bsa', 'auc', 'weight'] as const

export function initializeDoseLimits(doseLimits?: DoseLimitsMatrix | null): DoseLimitsMatrix {
  const result: Record<string, Record<string, number>> = {}
  for (const row of DOSE_LIMIT_ROWS) {
    result[row] = {}
    for (const col of DOSE_LIMIT_COLS) {
      result[row][col] = doseLimits?.[row]?.[col] ?? 0
    }
  }
  return result as DoseLimitsMatrix
}
export const isTypeSolution = (input: unknown) => checkCatalogType(input, CatalogType.enum.SOLUTION)

function buildCode(name: string | undefined) {
  return name?.slice(0, 3).toUpperCase() || ''
}

interface AutoFillCodePayload {
  isCodePristine: boolean
  name: string | undefined
  code: string | undefined
}
export function autoFillCode({isCodePristine, code, name}: AutoFillCodePayload) {
  if (!isCodePristine) return null
  const built = buildCode(name)
  if (code === built) return null
  return built
}

export function getDrugTradeNames(drug: DrugCatalogItem) {
  const medicationProfiles = drug?.medicationProfiles || []
  const tradeNames: TradeName[] = []

  for (const medicationProfile of medicationProfiles) {
    if (medicationProfile.tradeNames?.length)
      tradeNames.push(...medicationProfile.tradeNames.map(tradeName => ({...tradeName, medicationProfile})))
  }

  return tradeNames
}
